import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { getBlockSchedules, updateBlockSchedule, createCustomSchedule, deleteCustomSchedule } from "../../lib/api";

export function BlockScheduleTab() {
  const [blockSchedules, setBlockSchedules] = useState({ full_day: null, half_day: null, custom: [] });
  const [loading, setLoading] = useState(false);
  const [editingType, setEditingType] = useState("full_day");
  const [editBlocks, setEditBlocks] = useState([]);
  const [customDate, setCustomDate] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const res = await getBlockSchedules();
      setBlockSchedules(res.data);
      const sched = res.data[editingType];
      if (sched) setEditBlocks(sched.blocks || []);
    } catch { toast.error("Failed to load block schedules"); } finally { setLoading(false); }
  };

  useEffect(() => { fetchSchedules(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingType === "custom") {
        if (!customDate) { toast.error("Please select a date"); setSaving(false); return; }
        await createCustomSchedule({ date: customDate, blocks: editBlocks, label: `Custom - ${customDate}` });
        toast.success(`Custom schedule saved for ${customDate}`);
      } else {
        await updateBlockSchedule(editingType, { blocks: editBlocks });
        toast.success(`${editingType === "full_day" ? "Full Day" : "Half Day"} schedule updated`);
      }
      fetchSchedules();
    } catch { toast.error("Failed to save schedule"); } finally { setSaving(false); }
  };

  const handleBlockChange = (idx, field, value) => {
    const updated = [...editBlocks];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditBlocks(updated);
  };

  const handleAddBlock = () => {
    const n = editBlocks.length + 1;
    setEditBlocks([...editBlocks, { block_number: n, name: `Block ${n}`, start_time: "09:00", end_time: "09:45" }]);
  };

  const handleRemoveBlock = (idx) => {
    const updated = editBlocks.filter((_, i) => i !== idx);
    updated.forEach((b, i) => { b.block_number = i + 1; if (b.name.startsWith("Block ")) b.name = `Block ${i + 1}`; });
    setEditBlocks(updated);
  };

  return (
    <div className="space-y-6" data-testid="block-schedule-tab">
      <Card>
        <CardHeader>
          <CardTitle>Block Schedule</CardTitle>
          <CardDescription>Configure daily block times for full-day, half-day, and custom schedules</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading && <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}
          {blockSchedules.full_day && (
            <>
              <div className="flex gap-2 flex-wrap">
                {[{ value: "full_day", label: "Full Day" }, { value: "half_day", label: "Half Day" }, { value: "custom", label: "Custom Date" }].map(t => (
                  <Button key={t.value} variant={editingType === t.value ? "default" : "outline"}
                    className={editingType === t.value ? "bg-[#00305E]" : ""}
                    onClick={() => {
                      setEditingType(t.value);
                      if (t.value === "custom") setEditBlocks(blockSchedules.full_day?.blocks || []);
                      else setEditBlocks(blockSchedules[t.value]?.blocks || []);
                    }} data-testid={`schedule-type-${t.value}`}>{t.label}</Button>
                ))}
              </div>
              {editingType === "custom" && (
                <div className="space-y-2">
                  <Label>Date for Custom Schedule</Label>
                  <Input type="date" value={customDate} onChange={e => setCustomDate(e.target.value)} className="w-auto" data-testid="custom-schedule-date" />
                </div>
              )}
              <div className="space-y-3">
                <div className="grid grid-cols-[40px_1fr_120px_120px_40px] gap-2 items-center text-xs font-medium text-muted-foreground uppercase tracking-wider pb-2 border-b">
                  <span>#</span><span>Name</span><span>Start</span><span>End</span><span></span>
                </div>
                {editBlocks.map((block, idx) => (
                  <div key={idx} className="grid grid-cols-[40px_1fr_120px_120px_40px] gap-2 items-center" data-testid={`block-row-${idx}`}>
                    <span className="text-sm font-bold text-[#00305E]">{block.block_number}</span>
                    <Input value={block.name} onChange={e => handleBlockChange(idx, "name", e.target.value)} className="h-9" data-testid={`block-name-${idx}`} />
                    <Input type="time" value={block.start_time} onChange={e => handleBlockChange(idx, "start_time", e.target.value)} className="h-9" data-testid={`block-start-${idx}`} />
                    <Input type="time" value={block.end_time} onChange={e => handleBlockChange(idx, "end_time", e.target.value)} className="h-9" data-testid={`block-end-${idx}`} />
                    <button onClick={() => handleRemoveBlock(idx)} className="text-red-400 hover:text-red-600 p-1" title="Remove block">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={handleAddBlock} data-testid="add-block-btn">Add Block</Button>
                  <Button onClick={handleSave} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="save-schedule-btn">
                    {saving ? "Saving..." : "Save Schedule"}
                  </Button>
                </div>
              </div>
              {blockSchedules.custom?.length > 0 && (
                <div className="pt-4 border-t space-y-2">
                  <Label className="text-sm font-semibold">Existing Custom Schedules</Label>
                  {blockSchedules.custom.map((cs, idx) => (
                    <div key={cs.date ?? `custom-${idx}`} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{cs.label || cs.date}</p>
                        <p className="text-xs text-muted-foreground">{cs.blocks?.length || 0} blocks</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditingType("custom"); setCustomDate(cs.date); setEditBlocks(cs.blocks || []); }}>Edit</Button>
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={async () => { if (window.confirm(`Delete custom schedule for ${cs.date}?`)) { try { await deleteCustomSchedule(cs.date); toast.success("Deleted"); fetchSchedules(); } catch { toast.error("Failed"); } } }}>Delete</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {!blockSchedules.full_day && !loading && (
            <Button onClick={fetchSchedules} variant="outline">Load Block Schedules</Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
